import React from "react"; // For JSX
import { Container } from "@/components/ui/container";

// Placeholder Icon Components (replace with actual icons or use a library)
const IconCourseProfile = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M5 6h14M5 10h14M5 14h14M5 18h14"
    />
  </svg>
);

const IconMegaphone = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11 5.882V4.25a2.25 2.25 0 012.25-2.25h0A2.25 2.25 0 0115.5 4.25v1.632m0 0V19.5m0-13.618a2.25 2.25 0 00-2.25-2.25h0A2.25 2.25 0 0011 5.882m0 0H9.75m1.25 0V2.25A2.25 2.25 0 009.75 0h0A2.25 2.25 0 007.5 2.25v3.632m0 0H4.5m3 0V19.5m0-13.618a2.25 2.25 0 00-2.25-2.25h0A2.25 2.25 0 000 5.882M7.5 19.5V16.5a1.5 1.5 0 013 0v3m0 0V5.882"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 9h.008v.008H3.75V9zm0 3h.008v.008H3.75v-3zm0 3h.008v.008H3.75V15zm3.75-3h.008v.008H7.5v-3zm0 3h.008v.008H7.5V15zm3.75-3h.008v.008h-.008v-3zm0 3h.008v.008h-.008V15zm3.75-3h.008v.008h-.008v-3zm0 3h.008v.008h-.008V15z"
    />
  </svg>
);

// New Checkmark Icon (reusing from LeaguesSection for consistency if extracted globally later)
const IconCheck = () => (
  <svg
    className="h-5 w-5 text-green-500 mr-2 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M5 13l4 4L19 7"
    />
  </svg>
);

// Skeleton Preview Components
const SkeletonClubProfilePreview = () => (
  <div className="mt-4 p-3 border border-dashed rounded-md bg-muted/50">
    <div className="flex items-center space-x-3 mb-2">
      <div className="w-12 h-12 bg-gray-300 rounded-md flex items-center justify-center text-gray-500 text-2xl">
        🏞️
      </div>
      <div>
        <h4 className="text-sm font-semibold text-foreground/90">
          Green Valley Country Club
        </h4>
        <p className="text-xs text-muted-foreground">
          Your premier local golf destination.
        </p>
      </div>
    </div>
    <p className="text-xs text-muted-foreground">
      <span className="font-medium">Features:</span> 18-Hole Championship
      Course, Driving Range, Pro Shop, Restaurant...
    </p>
  </div>
);

const SkeletonClubAnnouncementPreview = () => (
  <div className="mt-4 p-3 border border-dashed rounded-md bg-muted/50">
    <h4 className="text-sm font-medium text-foreground/80 mb-2">
      Course Update
    </h4>
    <div className="p-2 rounded-md bg-background/70 border">
      <p className="text-xs font-semibold text-primary mb-0.5">
        New Post from Green Valley
      </p>
      <p className="text-xs text-muted-foreground">
        "Greens were just aerated this morning. Discounted rates available all
        week! Come check out the new pro shop merch while you're here."
      </p>
    </div>
    <p className="text-[10px] text-muted-foreground/80 mt-1.5 text-center">
      Seen by: 124 Subscribers & Local Golfers
    </p>
  </div>
);

export const ClubsSection = () => {
  return (
    <section
      id="clubs-section"
      className="w-full py-12 md:py-24 lg:py-32 relative overflow-hidden"
      style={{
        backgroundImage: "url(/bgs/bg4.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Light overlay for readability */}
      <div className="absolute inset-0 bg-background/85" />
      <Container className="relative z-10">
        <div className="text-center mb-12 md:mb-16 px-4 md:px-0">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm mb-4">
            <span>
              Club Manager Role • Separate from Member subscription
            </span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            For Course Owners: Manage Your Digital Presence
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto px-2 sm:px-0">
            Take control of how your course appears on Local Links. Update your
            amenities, manage photos, and connect directly with the golfers who
            play your course most often.
            <span className="block mt-2 text-primary font-medium">
              Players can tag your course in their posts — approve them to build
              your community feed.
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-12">
          {/* Feature 1: Your Course, Center Stage */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <IconCourseProfile />
              </div>
              <div>
                <h4 className="text-xl font-semibold mb-2">
                  Complete Course Management
                </h4>
                <p className="text-muted-foreground mb-3">
                  Ensure your course information is accurate and inviting.
                  Manage everything from hole-by-hole details to clubhouse
                  amenities and operating policies.
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Update course photos, descriptions, and facility details.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Manage scorecard data, par info, and local rules.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Highlight unique features like driving ranges or pro
                      shops.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <SkeletonClubProfilePreview />
            </div>
          </div>

          {/* Feature 2: Connect with Golfers */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <IconMegaphone />
              </div>
              <div>
                <h4 className="text-xl font-semibold mb-2">
                  Social Connection & Updates
                </h4>
                <p className="text-muted-foreground mb-3">
                  Turn your course page into a community hub. Post updates about
                  conditions, events, or promotions, and let golfers subscribe
                  to stay in the loop.
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Post photos and updates to your course feed.</span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Build a subscriber base of loyal local golfers.</span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Keep players informed about course conditions and events.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <SkeletonClubAnnouncementPreview />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
